import React from 'react' // eslint-disable-line
import {
  useProfile,
  useUIFunctions,
  useConfigState,
  Link,
  useIsAuthenticated,
  useLocalLabels,
} from '@sentecacommerce-theme/lib'
import styled, { css } from 'styled-components'
import { Search } from '../Search'
import Basket from '../../../../public/svg/cart'
import Heart from '../../../../public/svg/heart-bold'
import IconUser from '../../../../public/svg/user_outlined'
import Logo from '../../../../public/svg/LogoHippoland'

import Router from 'next/router'
import { IconMenu, IconTruck } from '../../../Common'
import { AnimatedWishlistCount } from '../AnimatedWishlistCount'
import { AnimatedBasketCount } from '../AnimatedBasketCount'
import { SuggestedMenu } from '../../navbar.props'
import { Labels } from '../../../MenuLayout/MenuLayout.props'

type Props = {
  categoryOutOfView: boolean
  desktopMenuClick: () => void
  drawerToggleHandler: () => void
  suggestedMenus: SuggestedMenu[]
  value: string
  searchBarData: any
  setValue: React.Dispatch<React.SetStateAction<string>>
  handleSearchBox: React.Dispatch<React.SetStateAction<boolean>>
}

export const NavHeader = (props: Props) => {
  const {
    categoryOutOfView,
    desktopMenuClick,
    drawerToggleHandler,
    suggestedMenus,
    value,
    setValue,
    handleSearchBox,
    searchBarData,
  } = props

  const goToProfilePage = () => {
    Router.push('/profile')
  }
  const labels = useLocalLabels<Labels>()
  const { enqueueView } = useUIFunctions()
  const config = useConfigState()
  const { isAuthenticated } = useIsAuthenticated()
  const { data: userData } = useProfile()

  const showCurrentUser = () => {
    return (
      <LoggedInUserIcon onClick={() => goToProfilePage()}>
        {!userData?.firstName ? (
          <span>{labels?.me}</span>
        ) : (
          <span>
            {userData?.firstName?.charAt(0)}
            {userData?.lastName?.charAt(0)}
          </span>
        )}
      </LoggedInUserIcon>
    )
  }

  return (
    <Container>
      <ToggleMenuWrapper
        categoryOutOfView={categoryOutOfView}
        onClick={desktopMenuClick}
        onMouseEnter={desktopMenuClick}
      >
        <IconMenu fill="currentColor" />
      </ToggleMenuWrapper>
      <ToggleMobileMenuWrapper>
        <IconMenu
          onClick={drawerToggleHandler}
          width="24"
          height="24"
          fill="currentColor"
        />
      </ToggleMobileMenuWrapper>
      <Link href="/" passHref>
        <LogoContainer
          aria-label={labels?.ariaLabelsAltsPlaceholders?.logo}
          categoryOutOfView={categoryOutOfView}
        >
          <Logo />
        </LogoContainer>
      </Link>
      <Link passHref href="/delivery-payment">
        <FreeShippingLabel>
          {labels.free}
          <IconTruck /> <span className="overPrice">{labels.overPrice}</span>
        </FreeShippingLabel>
      </Link>
      <SearchContainer categoryOutOfView={categoryOutOfView}>
        <Search
          searchData={searchBarData}
          value={value}
          setValue={setValue}
          handleSearchBox={handleSearchBox}
          suggestedMenus={suggestedMenus}
        />
      </SearchContainer>

      <IconsContainer>
        <IconWrapper>
          {!isAuthenticated ? (
            <IconUser
              onClick={() => enqueueView(config.views.auth)}
              fill="currentColor"
              width="24"
              height="24"
            />
          ) : (
            showCurrentUser()
          )}
        </IconWrapper>
        <IconWrapper relative>
          <Link href="/favorites" passHref>
            <a aria-label={labels?.ariaLabelsAltsPlaceholders?.favorites}>
              <AnimatedWishlistCount />
              <Heart width="24" height="24" fill="currentColor" />
            </a>
          </Link>
        </IconWrapper>
        <IconWrapper relative>
          <Link href="/basket" passHref>
            <a aria-label={labels?.ariaLabelsAltsPlaceholders?.basket}>
              <AnimatedBasketCount />
              <Basket width="26" height="26" fill="currentColor" />
            </a>
          </Link>
        </IconWrapper>
      </IconsContainer>
    </Container>
  )
}

const Container = styled.div.attrs({
  className: 'd-none d-md-flex align-items-center',
})`
  color: ${({ theme }) => theme.palette.silverTree};
`

const LogoContainer = styled.a<{ categoryOutOfView: boolean }>`
  outline: 0;
  line-height: 0;
  min-width: 12rem;
  max-width: 12rem;
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.primary};

  ${(props) =>
    props.categoryOutOfView &&
    css`
      min-width: 8rem;
      max-width: 8rem;
    `}
`

const FreeShippingLabel = styled.a.attrs({
  className:
    'd-none d-lg-flex justify-content-center mx-auto align-items-center',
})`
  cursor: pointer;
  left: 13rem;
  color: ${({ theme }) => theme.palette.seaGreen};
  border-radius: 20px;

  min-width: 19.1rem;

  font-size: 1.4rem;
  text-align: center;

  & svg {
    display: inline-block;
    max-height: 2rem;
    margin: 0 0.5rem;
  }

  &:hover {
    opacity: 0.7;
  }
`

const SearchContainer = styled.div<{ categoryOutOfView?: boolean }>`
  position: relative;
  margin: 0 auto;

  width: ${(props) =>
    props.categoryOutOfView
      ? 'calc(100% - 14.2rem - 6rem - 8rem - 12rem)'
      : 'calc(100% - 14.2rem - 12rem - 12rem)'};

  display: flex;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm.max}) {
    display: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    width: calc(100% - 14.2rem - 12rem - 28rem);
    margin: 0;
  }
`

const IconsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  list-style: none;

  max-width: 14.2rem;

  & > span svg {
    @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
      width: 2.4rem;
      height: 2.4rem;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm.max}) {
    margin-right: 1rem;
    padding-top: 0;
    padding-bottom: 0.5rem;
    margin-top: 0.5rem;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg.min}) {
    margin-left: auto;
  }
`

const IconWrapper = styled.span<{ relative?: boolean }>`
  cursor: pointer;
  margin-right: 1.5rem;
  position: ${(props) => (props.relative ? 'relative' : 'initial')};
  &:last-of-type {
    margin-right: 0;
  }

  & > svg {
    &:hover {
      & > path {
        fill: ${({ theme }) => theme.palette.burntSienna} !important;
      }
    }
  }
  & > a {
    &:hover {
      & > svg {
        & > path {
          fill: ${({ theme }) => theme.palette.burntSienna} !important;
        }
      }
    }
  }
`

const LoggedInUserIcon = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-center',
})`
  border-radius: 50%;
  background-color: ${({ theme }) => theme.palette.burntSienna};
  color: ${({ theme }) => theme.palette.white};
  font-size: 1.3rem;
  min-width: 3rem;
  min-height: 3rem;
  transition: background-color 0.3s, opacity 0.3s;

  & > span {
    text-transform: uppercase;
    margin-top: -0.1rem;
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.silverTree};
    opacity: 0.7;
  }
`

const ToggleMenuWrapper = styled.div<{ categoryOutOfView: boolean }>`
  margin-right: 2.4rem;
  display: ${(props) => (props.categoryOutOfView ? 'block' : 'none')};
  & > svg {
    width: 3.6rem;
    height: 3.6rem;
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md.max}) {
    display: none;
  }
`

const ToggleMobileMenuWrapper = styled.div`
  display: none;
  @media (max-width: ${({ theme }) =>
      theme.breakpoints.md.max}) and (min-width: ${({ theme }) =>
      theme.breakpoints.md.min}) {
    display: block;
    margin-right: 1.8rem;
    & > svg {
      width: 3.6rem;
      height: 3.6rem;
    }
  }
`
